import { openDB } from 'idb';
import { STORAGE_KEY } from './constants.js';
import { supabase } from './supabase.js';

const DB_NAME = 'lifeos';
const DB_VERSION = 1;
const STORE = 'state';
const KEY = 'main';
const REMOTE_TIMEOUT_MS = 10000;

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out after ${REMOTE_TIMEOUT_MS / 1000}s`)), REMOTE_TIMEOUT_MS);
    })
  ]);
}

function getDb() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    }
  });
}

export async function loadFromDb() {
  try {
    const db = await getDb();
    const saved = await db.get(STORE, KEY);
    if (saved) return saved;
    // Migrate any existing localStorage data on first run
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      await db.put(STORE, parsed, KEY);
      localStorage.removeItem(STORAGE_KEY);
      return parsed;
    }
    return null;
  } catch {
    // IDB unavailable — fall back to localStorage
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}

export async function saveToDb(data) {
  try {
    const db = await getDb();
    await db.put(STORE, data, KEY);
  } catch {
    // IDB unavailable — fall back to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }
}

export async function clearDb() {
  try {
    const db = await getDb();
    await db.delete(STORE, KEY);
  } catch {}
  localStorage.removeItem(STORAGE_KEY);
}

// Load from Supabase (primary), cache result in IDB, fall back to IDB if offline
export async function loadUserData(userId) {
  try {
    const { data, error } = await withTimeout(
      supabase
        .from('user_data')
        .select('payload')
        .eq('user_id', userId)
        .single(),
      'Remote load'
    );
    if (!error && data?.payload) {
      await saveToDb(data.payload);
      return data.payload;
    }
    if (error && error.code !== 'PGRST116') {
      console.error('Remote load failed:', error);
    }
  } catch (err) {
    console.error('Remote load failed:', err);
  }
  return loadFromDb();
}

// Save to Supabase only — IDB is written separately in App's save effect
export async function saveUserData(userId, appData) {
  const { error } = await withTimeout(
    supabase.from('user_data').upsert(
      { user_id: userId, payload: appData, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    ),
    'Remote save'
  );
  if (error) {
    console.error('Remote save failed:', error);
    throw error;
  }
  return true;
}
